import TutorProfileAvatar from './TutorProfileAvatar';
import SessionMaterialsList from './SessionMaterialsList';
import {
  getBookingStatusLabel,
  getBookingSubjectName,
} from '../utils/bookingDisplay';

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatTime = (value) =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const getLearnerFirstName = (session) =>
  session.learnerLabel?.trim().split(/\s+/)[0] ||
  session.student?.firstName ||
  'Learner';

const getTutorFirstName = (session) =>
  session.tutor?.user?.firstName ||
  session.tutor?.fullName?.trim().split(/\s+/)[0] ||
  'Tutor';

const statusClass = (status) => {
  if (status === 'confirmed') return 'bg-green-100 text-green-800';
  if (status === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
};

const LearnerUpcomingSessionsView = ({
  sessions,
  selectedSession,
  onSelect,
  onCancel,
}) => (
  <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
    <section
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-label="Upcoming and pending sessions"
    >
      <div className="border-b border-gray-200 px-5 py-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Upcoming and Pending Sessions
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Select a session to view its details.
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        {sessions.map((session) => {
          const selected = selectedSession?.id === session.id;
          return (
            <button
              key={session.id}
              id={`session-${session.id}`}
              type="button"
              onClick={() => onSelect(session.id)}
              className={`w-full px-5 py-4 text-left transition-colors ${
                selected
                  ? 'bg-blue-50 ring-2 ring-inset ring-brand-medium-blue/30'
                  : 'hover:bg-gray-50'
              }`}
              aria-pressed={selected}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <TutorProfileAvatar
                  profilePic={session.tutor?.profilePic}
                  name={getTutorFirstName(session)}
                  className="h-11 w-11"
                  showInitialFallback
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {getLearnerFirstName(session)}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(session.status)}`}
                    >
                      {getBookingStatusLabel(session.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {formatDate(session.startTime)} ·{' '}
                    {formatTime(session.startTime)} –{' '}
                    {formatTime(session.endTime)}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    {getBookingSubjectName(session)} · Tutor:{' '}
                    {getTutorFirstName(session)}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>

    <aside className="h-fit rounded-xl border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-24">
      {selectedSession ? (
        <>
          <h2 className="text-xl font-semibold text-gray-900">
            Session Details
          </h2>

          <div className="mt-5 flex items-center gap-3 border-b border-gray-100 pb-5">
            <TutorProfileAvatar
              profilePic={selectedSession.tutor?.profilePic}
              name={getTutorFirstName(selectedSession)}
              className="h-12 w-12"
              showInitialFallback
            />
            <div>
              <p className="font-semibold text-gray-900">
                {getTutorFirstName(selectedSession)}
              </p>
              <p className="text-sm text-gray-500">
                Tutor for {getLearnerFirstName(selectedSession)}
              </p>
            </div>
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-1">
            <div>
              <dt className="font-medium text-gray-500">Subject</dt>
              <dd className="mt-1 text-gray-900">
                {getBookingSubjectName(selectedSession)}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Date</dt>
              <dd className="mt-1 text-gray-900">
                {formatDate(selectedSession.startTime)}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Time</dt>
              <dd className="mt-1 text-gray-900">
                {formatTime(selectedSession.startTime)} –{' '}
                {formatTime(selectedSession.endTime)}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-gray-900">
                {getBookingStatusLabel(selectedSession.status)}
              </dd>
            </div>
          </dl>

          <div className="mt-6 border-t border-gray-100 pt-5">
            <h3 className="mb-3 font-semibold text-gray-900">Materials</h3>
            <SessionMaterialsList materials={selectedSession.materials} />
          </div>

          <div className="mt-6 border-t border-gray-100 pt-5">
            <h3 className="mb-2 font-semibold text-gray-900">Comments</h3>
            <p className="whitespace-pre-wrap text-sm text-gray-600">
              {selectedSession.notes || 'No comments added.'}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 border-t border-gray-100 pt-5">
            {selectedSession.zoomJoinLink && (
              <a
                href={selectedSession.zoomJoinLink}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-brand-medium-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark-blue"
              >
                Join Session
              </a>
            )}
            {selectedSession.status === 'confirmed' && (
              <button
                type="button"
                onClick={() => onCancel(selectedSession)}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
              >
                Cancel Booking
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">
          Select a session to view its details.
        </p>
      )}
    </aside>
  </div>
);

export default LearnerUpcomingSessionsView;
